import React from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Button } from '@/components/atoms/button'
import { SalesConfirmErrorResponse, SalesConfirmErrorReservation, SalesConfirmErrorCode } from '@/models/sales-manager/sales-confirm-error'

const ERROR_MESSAGE_KEYS: Record<SalesConfirmErrorCode, string> = {
  UNPAID_SALES_REMAIN: 'salesConfirm.error.UNPAID_SALES_REMAIN',
  DISCOUNT_SUBJECT_PAYMENT_NOT_SELECTED: 'salesConfirm.error.DISCOUNT_SUBJECT_PAYMENT_NOT_SELECTED',
}

type SalesConfirmErrorModalProps = {
  error: SalesConfirmErrorResponse
  onClose: () => void
}

const getDisplayLabel = (item: SalesConfirmErrorReservation): { linkText: string; reservationId: string } => {
  const name = item.guest_name?.trim()
  return {
    linkText: name || '-',
    reservationId: item.reservation_id,
  }
}

export const SalesConfirmErrorModal: React.FC<SalesConfirmErrorModalProps> = ({ error, onClose }) => {
  const { t } = useTranslation()
  const messageKey = ERROR_MESSAGE_KEYS[error.errorCode] ?? 'salesConfirm.error.fallback'
  const message = t(messageKey)
  const messageWithBr = message.replace(/\n/g, '<br />')

  return (
    <div css={modalWrapperStyle}>
      <div css={modalContentStyle}>
        <p css={modalTitleStyle}>{t('Sales confirm error')}</p>
        <div css={messageWrapperStyle}>
          <p css={messageStyle} dangerouslySetInnerHTML={{ __html: messageWithBr }} />
        </div>
        <ul css={listStyle}>
          {error.reservations.map(item => {
            const { linkText, reservationId } = getDisplayLabel(item)
            return (
              <li key={item.reservation_id} css={listItemStyle}>
                <Link to={`/reservation/${reservationId}?reservationId=${reservationId}`} css={linkStyle}>
                  {linkText}
                </Link>
                （{reservationId}）
              </li>
            )
          })}
        </ul>
        <div css={footerButtonWrapperStyle}>
          <Button width={185} height={38} buttonType={1} onClick={onClose}>
            {t('Close')}
          </Button>
        </div>
      </div>
    </div>
  )
}

const modalWrapperStyle = css({
  boxShadow: '0px 0px 6px #0000001A',
  border: '1px solid #F2F2F2',
  borderRadius: 5,
  width: '100vw',
  height: '100vh',
  background: 'rgba(39, 39, 39, 0.5)',
  position: 'fixed',
  top: 0,
  left: 0,
  zIndex: 103,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
})

const modalContentStyle = css({
  width: 688,
  maxWidth: '90vw',
  padding: '45px 32px 32px',
  background: '#fff',
  borderRadius: 5,
})

const modalTitleStyle = css({
  marginBottom: 16,
  fontSize: 18,
  letterSpacing: 2.7,
  textAlign: 'center',
  fontWeight: 'bold',
})

const messageWrapperStyle = css({
  marginBottom: 24,
})

const messageStyle = css({
  marginBottom: 0,
  fontSize: 14,
  letterSpacing: 2.4,
  lineHeight: 1.6,
  color: '#272727',
})

const listStyle = css({
  width: '100%',
  maxHeight: 240,
  overflowY: 'auto',
  padding: 16,
  marginBottom: 24,
  background: '#f5f5f5',
  border: '1px solid #ccc',
  borderRadius: 4,
  listStyle: 'none',
})

const listItemStyle = css({
  marginBottom: 8,
  '&:last-child': { marginBottom: 0 },
})

const linkStyle = css({
  fontSize: 14,
  color: '#F2A40B',
  fontWeight: 'bold',
  textDecoration: 'none',
  '&:hover': { color: '#d9920a' },
})

const footerButtonWrapperStyle = css({
  display: 'flex',
  justifyContent: 'center',
})
